{
  "title": "How to Manage Platform Super Admins",
  "description": "Grant or revoke another user's platform super admin status.",
  "headings": [
    "What You'll Accomplish",
    "Applies To",
    "Before You Start",
    "Steps",
    "What You'll See",
    "FAQ"
  ],
  "summary": "When you're done, that user immediately gains or loses platform super admin privileges — the change takes effect right away.",
  "roleNote": "Platform Super Admin",
  "prerequisites": ["You are yourself a super admin", "You know the target user's account"],
  "pathLabel": "Platform Administration → Administrators",
  "steps": [
    {
      "title": "1. Find the target user",
      "description": "Search or browse the Administrators list and confirm whether the user currently has super admin status."
    },
    {
      "title": "2a. Grant super admin",
      "description": "For a user who doesn't yet have this status, click \"Grant\". This action is idempotent — running it again on someone who's already a super admin produces no error and no duplicate record."
    },
    {
      "title": "2b. Revoke super admin",
      "description": "For a user who already has this status, click \"Revoke\" — also idempotent. The system first checks whether at least one active super admin would remain on the platform; if the target is the last one, the revoke is rejected (409 error) to prevent the platform from ending up with zero super admins."
    },
    {
      "title": "3. Confirm the result",
      "description": "On success, that user's role badge updates immediately. If the action is blocked by the \"last active super admin\" protection, the screen clearly states the reason — you'll need to grant another user super admin status first before this one can be revoked."
    }
  ],
  "afterward": [
    "The target user's super admin status takes effect (or is revoked) immediately",
    "The action is recorded in the platform audit log",
    "If blocked by the protection rule, nothing about the platform's state changes"
  ],
  "nextSteps": "After granting, it's worth asking the person to sign in and confirm their new privileges took effect; after revoking, they lose the related admin entry points on their very next action.",
  "faq": [
    {
      "q": "Why can't I revoke someone's super admin status?",
      "a": "The most common reason is the \"last active super admin\" protection: the system never allows the platform to end up with zero active super admins, so if the target is currently the only one, the revoke request is rejected (409 LAST_SUPER_ADMIN_PROTECTED). Grant super admin status to another user first, then come back and revoke this one."
    },
    {
      "q": "Does clicking \"Grant\" twice cause an error or a duplicate record?",
      "a": "No. Both grant and revoke are idempotent — granting to someone who's already a super admin, or revoking from someone who already isn't one, just reports \"no change\", with no error and no duplicate record."
    },
    {
      "q": "What's the difference between super admin and a regular \"platform permission\"?",
      "a": "A super admin automatically has every named platform capability, with nothing to select individually. A regular admin's visible features and actions are instead driven by whichever individual capabilities (e.g. audit log read, account management) were actually granted to them."
    }
  ]
}
